import {
  Autocomplete,
  Box,
  MenuItem,
  TextField,
  type AutocompleteRenderInputParams,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useMemo, type ChangeEvent } from "react";
import {
  type ApiOrderOption,
  type SetApiOrderField,
} from "../hooks/use-api-order-form";
import ApiOrderSection from "./api-order-section";

const styles = {
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 2,
  } satisfies SxProps<Theme>,
};

const getOptionLabel = (option: ApiOrderOption) => option.name;

const isOptionEqualToValue = (option: ApiOrderOption, value: ApiOrderOption) =>
  option.uuid === value.uuid;

type ApiOrderTargetSectionProps = {
  readonly companyUuid: string;
  readonly billingContactUuid: string;
  readonly serviceLevelUuid: string;
  readonly companyOptions: readonly ApiOrderOption[];
  readonly contactOptions: readonly ApiOrderOption[];
  readonly serviceOptions: readonly ApiOrderOption[];
  readonly companiesLoading: boolean;
  readonly contactsLoading: boolean;
  readonly setField: SetApiOrderField;
  readonly onCompanyChange: (companyUuid: string) => void;
  readonly onBillingContactChange: (billingContactUuid: string) => void;
};

/**
 * Picks who the order is created for: the company, the contact billed for it,
 * and optionally the service level.
 */
const ApiOrderTargetSection = ({
  companyUuid,
  billingContactUuid,
  serviceLevelUuid,
  companyOptions,
  contactOptions,
  serviceOptions,
  companiesLoading,
  contactsLoading,
  setField,
  onCompanyChange,
  onBillingContactChange,
}: ApiOrderTargetSectionProps) => {
  const handleCompanyChange = useCallback(
    (_: unknown, value: ApiOrderOption | null) => {
      onCompanyChange(value?.uuid ?? "");
    },
    [onCompanyChange],
  );

  const handleBillingContactChange = useCallback(
    (_: unknown, value: ApiOrderOption | null) => {
      onBillingContactChange(value?.uuid ?? "");
    },
    [onBillingContactChange],
  );

  const handleServiceLevelChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setField("serviceLevelUuid", event.target.value);
    },
    [setField],
  );

  const renderCompanyInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField {...params} required label="Company" />
    ),
    [],
  );

  const renderBillingContactInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField {...params} required label="Billing contact" />
    ),
    [],
  );

  const selectedCompany = useMemo(
    () => companyOptions.find((option) => option.uuid === companyUuid) ?? null,
    [companyOptions, companyUuid],
  );

  const selectedBillingContact = useMemo(
    () =>
      contactOptions.find((option) => option.uuid === billingContactUuid) ??
      null,
    [contactOptions, billingContactUuid],
  );

  return (
    <ApiOrderSection title="Target">
      <Box sx={styles.row}>
        <Autocomplete
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          loading={companiesLoading}
          loadingText="Loading companies…"
          noOptionsText="No companies found."
          options={companyOptions}
          renderInput={renderCompanyInput}
          size="small"
          value={selectedCompany}
          onChange={handleCompanyChange}
        />
        <Autocomplete
          disabled={companyUuid === ""}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          loading={contactsLoading}
          loadingText="Loading contacts…"
          noOptionsText="This company has no active contacts."
          options={contactOptions}
          renderInput={renderBillingContactInput}
          size="small"
          value={selectedBillingContact}
          onChange={handleBillingContactChange}
        />
        <TextField
          select
          helperText="Left unset, the create path applies the company's default for the stop combination."
          label="Service level"
          size="small"
          value={serviceLevelUuid}
          onChange={handleServiceLevelChange}
        >
          <MenuItem value="">Unset</MenuItem>
          {serviceOptions.map((service) => (
            <MenuItem key={service.uuid} value={service.uuid}>
              {service.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </ApiOrderSection>
  );
};

export default ApiOrderTargetSection;
